"use client";
import React, { useMemo, useState } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import { useTranslations } from "next-intl";

import { useParcels } from "@/context/Parcels.context";
import { useTheme } from "@/context/Theme.context";

import ParcelsTable from "@/components/dashboard/parcels/get-parcels-table/get-parcels-table";
import ParcelsFilterBox from "@/components/dashboard/parcels/parcels-filter/parcels-filter-box";
import {
  Category,
  ShopWithCountry,
  TransportationWrapper,
} from "@/components/dashboard/parcels/get-parcels-table/components";

import { useParcelsRequest } from "@/api/useParcelsRequest";
import { ModalType, ParcelStatus, Theme } from "@/enums";
import { IShipment } from "@/types/apiResponse.types";
import type { IParcelFilter } from "@/types/parcel-filter.types";
import { isInDateRange } from "@/utils/parcel-filter.utils";
import { Actions } from "@/components/common";
import { InfoCircleIcon } from "@/components/assets/icons";

const columnHelper = createColumnHelper<IShipment>();

const ExpectedParcels = () => {
  const t = useTranslations();
  const { openParcelsModal } = useParcels();

  const theme = useTheme();

  const [filters, setFilters] = useState<IParcelFilter>({
    country: [],
    category: [],
    transport: [],
    status: [],
    registrationDate: { start: null, end: null },
    sendDate: { start: null, end: null },
    deliveryDate: { start: null, end: null },
  });
  const [search, setSearch] = useState("");

  const { data, isLoading } = useParcelsRequest({
    shipmentStatus: ParcelStatus.COMMON_STATUS_STATUS_WAITING_TO_RECEIVE,
  });

  const parcelsData = data?.data?.data || [];

  const filteredParcelsData = useMemo<IShipment[]>(() => {
    return parcelsData.filter((item) => {
      const isCountryMatch =
        filters.country.length === 0 ||
        filters.country.includes(item.countryId);
      const isCategoryMatch =
        filters.category.length === 0 ||
        filters.category.includes(item.shipmentTypeId);
      const isTransportMatch =
        filters.transport.length === 0 ||
        filters.transport.includes(item.deliveryFromFromcountryTypeId);
      const isRegistrationDateMatch = isInDateRange(
        item.dateCreated?.date,
        filters.registrationDate,
      );
      const searchLower = search.toLowerCase();
      const isSearchMatch =
        item?.trCode?.toLowerCase().includes(searchLower) ||
        item?.description?.toLowerCase().includes(searchLower) ||
        item?.onlineShop?.toLowerCase().includes(searchLower);
      return (
        isCountryMatch &&
        isCategoryMatch &&
        isTransportMatch &&
        isRegistrationDateMatch &&
        isSearchMatch
      );
    });
  }, [parcelsData, filters, search]);

  const columns = useMemo(() => {
    const baseColumns = [
      columnHelper.accessor((row) => row, {
        id: "shop",
        header: () => <></>,
        cell: (info) => (
          <ShopWithCountry
            shop={info.getValue()?.onlineShop}
            country={info.getValue()?.countryId}
          />
        ),
        size: 10,
      }),
      columnHelper.accessor((row) => row?.trCode, {
        id: "trCode",
        header: () => <span>{t("tracking_number")}</span>,
        cell: (info) => <span>{info.getValue() || t("no_tracking_code")}</span>,
      }),
      columnHelper.accessor(
        (row) =>
          theme === Theme.UK ? row?.shipmentTypeParentId : row?.shipmentTypeId,
        {
          id: "shipmentTypeId",
          header: () => <span>{t("category")}</span>,
          size: 50,
          cell: (info) => (
            <div className="pl-4">
              <Category
                category={info.getValue()}
                shipmentTypeParentId={info.getValue()}
              />
            </div>
          ),
        },
      ),
      columnHelper.accessor((row) => row?.declaredUsd, {
        id: "priceUsd",
        size: 100,
        header: () => <span>{t("product_price")}</span>,
        cell: (info) => <span>${info.getValue() || 0}</span>,
      }),
      ...(theme === Theme.UK
        ? [
            columnHelper.accessor((row) => row?.description, {
              id: "description",
              header: () => <span>{t("description")}</span>,
              cell: (info) => <span>{info.getValue() || ""}</span>,
            }),
          ]
        : []),
      columnHelper.accessor((row) => row?.receiveTcountryExpectedDate, {
        id: "transportationInfo",
        header: () => <span>{t("transportation_info")}</span>,
        cell: (info) => (
          <TransportationWrapper
            mode={info?.row?.original?.deliveryFromFromcountryTypeId}
            estimatedArrival={info.getValue()?.date}
            showDate={false}
            countryId={info?.row?.original?.countryId}
          />
        ),
      }),
    ];

    baseColumns.push(
      columnHelper.accessor((row) => row, {
        id: "actions",
        header: () => <></>,
        cell: (info) => (
          <Actions
            isZeroIndex={info?.row?.index === 0}
            onDelete={() => {
              openParcelsModal({
                id: Number(info?.getValue().shipmentId),
                modalType: ModalType.DELETE,
              });
            }}
            onEdit={
              info?.getValue()?.commonStatusCode ===
                ParcelStatus.COMMON_STATUS_STATUS_WAITING_TO_RECEIVE ||
              info?.getValue()?.commonStatusCode ===
                ParcelStatus.COMMON_STATUS_RECEIVED_AT_THE_INSTOCK
                ? () => {
                    openParcelsModal({
                      id: Number(info?.getValue().shipmentId),
                      modalType: ModalType.EDIT,
                    });
                  }
                : undefined
            }
          />
        ),
      }),
    );

    return baseColumns;
  }, [theme]);

  return (
    <>
      {theme === Theme.UK && (
        <ParcelsFilterBox
          filters={filters}
          onFilterChange={setFilters}
          searchValue={search}
          onSearchChange={setSearch}
        />
      )}

      <ParcelsTable
        columns={columns}
        data={filteredParcelsData}
        isLoading={isLoading}
      />

      <p
        style={{
          color: "rgb(107 114 128)",
          fontSize: "14px",
          textAlign: "center",
          marginTop: "30px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "6px",
        }}
      >
        <InfoCircleIcon style={{ width: "16px", height: "16px" }} />
        {t("unregisted_parcel_info_text")}
      </p>
    </>
  );
};

export default ExpectedParcels;
